import type { JSX, ReactNode } from "react";
import type { PortfolioSummary } from "@finance-os/shared-types/api";

import { usePortfolioFormat } from "../../lib/use-portfolio-format.js";
import { Gauge } from "../Gauge.js";
import { Money } from "../Money.js";
import { StatCard } from "../StatCard.js";
import { Pl } from "./Pl.js";
import type { PanelQuery } from "./PanelCard.js";

const NONE = "—";

/** Headline stats: value, unrealized and realized P&L, cash vs invested, and the change over the period. */
export function PortfolioHeadline({
  query,
  period,
}: {
  query: PanelQuery<PortfolioSummary>;
  period: string;
}): JSX.Element {
  const fmt = usePortfolioFormat();
  const s = query.data;
  const change = s?.period_change ?? null;
  const dayChange = s?.day_change_eur_minor ?? null;
  let changeSub: ReactNode;
  if (s && (change === null || dayChange === null)) changeSub = "needs two days of history";
  else if (dayChange !== null) changeSub = <>Last day <Pl minor={dayChange} /></>;

  return (
    <div className="mb-4 grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4">
      <StatCard
        label="Portfolio value"
        value={s ? <Money amountMinor={s.nav_eur_minor} native="EUR" /> : NONE}
        sub={s ? `${String(s.position_count)} positions` : undefined}
      />
      <StatCard
        label="Unrealized P&L"
        value={s ? <Pl minor={s.unrealized_pl_eur_minor} pct={s.unrealized_pl_pct} /> : NONE}
        sub="on the open positions' cost"
      />
      <StatCard
        label="Realized P&L (YTD)"
        value={s ? <Pl minor={s.realized_ytd_eur_minor} /> : NONE}
        sub="sales this calendar year"
      />
      <StatCard
        label="Cash vs invested"
        value={s ? `${fmt.pct(s.invested_pct)} invested` : NONE}
        sub={s ? <>Cash <Money amountMinor={s.cash_eur_minor} native="EUR" /> · {fmt.pct(s.cash_pct)}</> : undefined}
      >
        {s && <Gauge value={s.invested_pct / 100} tone="brand" />}
      </StatCard>
      <StatCard
        label={`Change · ${period}`}
        value={change ? <Pl minor={change.change_eur_minor} pct={change.change_pct} /> : NONE}
        sub={changeSub}
      />
    </div>
  );
}
